import React, { memo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { PipelineItem, DateRange, PipelineType } from '../types';
import { filterDataByDateRange, filterDataByPipeline, calculateAverageTime } from '../utils/dataUtils';
import DataTable from './DataTable';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Mail, BoxIcon, PlusCircle, PenLine, CheckCircle } from 'lucide-react';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from './ui/chart';
// Define color constants to match Overview
const COLORS = {
  forms: {
    current: '#515B3A',
    previous: '#515B3A80' // 50% opacity
  },
  letters: {
    current: '#F78154',
    previous: '#F7815480' // 50% opacity
  },
  memos: {
    current: '#553E4E',
    previous: '#553E4E80' // 50% opacity
  }
};
// Legend colors for the key
const LEGEND_COLORS = {
  previous: '#D1D5DB',
  current: '#4B5563' // dark grey
};
// Custom legend renderer
const renderLegend = (props: any) => {
  const {
    payload
  } = props;
  return <div className="flex justify-center items-center gap-6 text-sm pt-4" data-id="element-144">
      {payload.map((entry: any, index: number) => <div key={`legend-${index}`} className="flex items-center gap-2" data-id="element-145">
          <div className="w-3 h-3 rounded-sm" style={{
        backgroundColor: LEGEND_COLORS[entry.value]
      }} data-id="element-146" />
          <span data-id="element-147">
            {entry.value === 'previous' ? 'Previous Period' : 'Current Period'}
          </span>
        </div>)}
    </div>;
};
interface PipelineDetailProps {
  pipelineType: PipelineType;
  data: PipelineItem[];
  dateRange: DateRange;
}
const PipelineDetail: React.FC<PipelineDetailProps> = ({
  pipelineType,
  data,
  dateRange
}) => {
  const pipelineTitle = {
    form: 'Forms',
    letter: 'Letters',
    memo: 'Memos'
  }[pipelineType];
  const pipelineIcon = {
    form: FileText,
    letter: Mail,
    memo: BoxIcon
  }[pipelineType];
  const pipelineColor = {
    form: COLORS.forms.current,
    letter: COLORS.letters.current,
    memo: COLORS.memos.current
  }[pipelineType];
  const pipelinePreviousColor = {
    form: COLORS.forms.previous,
    letter: COLORS.letters.previous,
    memo: COLORS.memos.previous
  }[pipelineType];
  const Icon = pipelineIcon;
  const filteredData = filterDataByPipeline(filterDataByDateRange(data, dateRange), pipelineType);
  // Get previous period data
  const previousStartDate = new Date(dateRange.startDate.getTime() - (dateRange.endDate.getTime() - dateRange.startDate.getTime()));
  const previousEndDate = new Date(dateRange.startDate);
  const previousPeriodData = filterDataByPipeline(filterDataByDateRange(data, {
    ...dateRange,
    startDate: previousStartDate,
    endDate: previousEndDate
  }), pipelineType);
  // Calculate metrics
  const totalItems = filteredData.length;
  const createdItems = filteredData.filter(item => item.createdAt >= dateRange.startDate && item.createdAt <= dateRange.endDate).length;
  const draftedItems = filteredData.filter(item => item.firstDraftAt && item.firstDraftAt >= dateRange.startDate && item.firstDraftAt <= dateRange.endDate).length;
  const approvedItems = filteredData.filter(item => item.approvalAt && item.approvalAt >= dateRange.startDate && item.approvalAt <= dateRange.endDate).length;
  // Calculate previous period metrics
  const previousCreatedItems = previousPeriodData.filter(item => item.createdAt >= previousStartDate && item.createdAt <= previousEndDate).length;
  const previousDraftedItems = previousPeriodData.filter(item => item.firstDraftAt && item.firstDraftAt >= previousStartDate && item.firstDraftAt <= previousEndDate).length;
  const previousApprovedItems = previousPeriodData.filter(item => item.approvalAt && item.approvalAt >= previousStartDate && item.approvalAt <= previousEndDate).length;
  // Calculate percentage changes
  const calculatePercentageChange = (previous: number, current: number): number => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round((current - previous) / previous * 100);
  };
  const createdChange = calculatePercentageChange(previousCreatedItems, createdItems);
  const draftedChange = calculatePercentageChange(previousDraftedItems, draftedItems);
  const approvedChange = calculatePercentageChange(previousApprovedItems, approvedItems);
  // Calculate average times for current period
  const avgDraftTime = calculateAverageTime(filteredData, 'createdAt', 'firstDraftAt');
  const avgApprovalTime = calculateAverageTime(filteredData, 'firstDraftAt', 'approvalAt');
  // Calculate average times for previous period
  const prevAvgDraftTime = calculateAverageTime(previousPeriodData, 'createdAt', 'firstDraftAt');
  const prevAvgApprovalTime = calculateAverageTime(previousPeriodData, 'firstDraftAt', 'approvalAt');
  // Prepare timing data for the horizontal bar chart
  const timingData = [{
    name: 'Time to First Draft',
    current: Math.round(avgDraftTime * 10) / 10,
    previous: Math.round(prevAvgDraftTime * 10) / 10
  }, {
    name: 'Time to Approval',
    current: Math.round(avgApprovalTime * 10) / 10,
    previous: Math.round(prevAvgApprovalTime * 10) / 10
  }];
  const chartConfig = {
    current: {
      label: 'Current Period',
      color: pipelineColor
    },
    previous: {
      label: 'Previous Period',
      color: pipelinePreviousColor
    }
  } satisfies ChartConfig;
  // Define metrics cards data with specific icons
  const metricsCards = [{
    title: 'Created',
    icon: PlusCircle,
    value: createdItems,
    change: createdChange
  }, {
    title: 'Drafted',
    icon: PenLine,
    value: draftedItems,
    change: draftedChange
  }, {
    title: 'Approved',
    icon: CheckCircle,
    value: approvedItems,
    change: approvedChange
  }];
  return <div data-id="element-148">
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-149">
        {metricsCards.map(card => <div key={card.title} className="rounded-lg border p-4" style={{
        borderColor: pipelineColor
      }} data-id="element-150">
            <div className="flex justify-between items-center" data-id="element-151">
              <div className="flex items-center gap-2" data-id="element-152">
                <div className="p-2 rounded-lg" style={{
              backgroundColor: `${pipelineColor}50`
            }} data-id="element-153">
                  <card.icon className="w-5 h-5" style={{
                color: pipelineColor
              }} data-id="element-154" />
                </div>
                <h3 className="font-medium" data-id="element-155">{card.title}</h3>
              </div>
              <div className="flex items-center gap-2" data-id="element-156">
                <div className="text-xl font-semibold" data-id="element-157">{card.value}</div>
                <div className={`text-sm font-medium ${card.change >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-158">
                  {card.change >= 0 ? '↑' : '↓'} {Math.abs(card.change)}%
                </div>
              </div>
            </div>
          </div>)}
      </div>
      {/* Horizontal Timing data chart */}
      <Card className="mb-8" data-id="element-159">
        <CardHeader className="pb-3" data-id="element-160">
          <CardTitle data-id="element-161">Processing Time</CardTitle>
          
        </CardHeader>
        <CardContent data-id="element-162">
          <ChartContainer config={chartConfig} className="h-[200px]" data-id="element-163">
            <ResponsiveContainer width="100%" height="100%" data-id="element-164">
              <BarChart data={timingData} layout="vertical" margin={{
              top: 5,
              right: 30,
              left: 100,
              bottom: 20
            }} barGap={4} data-id="element-165">
                <CartesianGrid horizontal={true} vertical={false} strokeDasharray="3 3" data-id="element-166" />
                <XAxis type="number" tickLine={false} axisLine={false} tickMargin={10} label={{
                value: 'Days',
                position: 'insideBottom',
                offset: -10
              }} data-id="element-167" />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tickMargin={10} data-id="element-168" />
                <ChartTooltip cursor={false} content={<ChartTooltipContent data-id="element-170" />} data-id="element-169" />
                <Legend content={renderLegend} data-id="element-171" />
                <Bar name="previous" dataKey="previous" fill="var(--color-previous)" radius={[0, 4, 4, 0]} barSize={20} data-id="element-172" />
                <Bar name="current" dataKey="current" fill="var(--color-current)" radius={[0, 4, 4, 0]} barSize={20} data-id="element-173" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>
      <div className="mt-8" data-id="element-174">
        <DataTable data={filterDataByPipeline(data, pipelineType)} dateRange={dateRange} pipelineType={pipelineType} data-id="element-175" />
      </div>
    </div>;
};
export default PipelineDetail;